import {
  createGesture,
  IonAvatar,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonListHeader,
  IonText,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { search as searchIcon, calendarOutline, imageOutline } from 'ionicons/icons';
import React, { useEffect } from 'react';
import { UseInfiniteQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import ContentGeneric from '@components/Content';
import Layout from '@components/Layout';
import InfiniteList from '@components/List';
import { Meta } from '@custom-types/Response';
import RoutePath from '@custom-types/RoutePath';
import { UserRole } from '@custom-types/UserSession';
import { formatDateRange } from '@services/date-service';
import { Locale } from '@services/language-service';
import { generateRoute } from '@services/navigation-service';
import { getLocale } from '@state/system';
import { getRole } from '@state/user';

import { Event, EventGroup, HAPPENING, UPCOMING, CONCLUDED } from './service';

const ID = 'events';
const SWIPE_TO_GO_BACK_PRIORITY = 40;

const ManageMobiButton = styled(IonButton)`
  margin: 16px;
`;

const Content = styled(ContentGeneric)`
  padding: 50px 16px 0;
  text-align: center;
`;

const Title = styled(IonText)`
  margin-bottom: 16px;
`;

const Icon = styled(IonIcon)`
  color: #ff9e1b;
  width: 138px;
  height: 138px;
`;

const ImageIcon = styled(IonIcon)`
  width: 40px;
  height: 40px;
`;

const Avatar = styled(IonAvatar)`
  --border-radius: unset;
`;

const selector = createSelector(getLocale, getRole, (locale, role) => ({ locale, role }));

const GROUPS: EventGroup[] = [HAPPENING, UPCOMING, CONCLUDED];

interface ListLayoutProps {
  eventsQueryResult: UseInfiniteQueryResult<{
    data: Event[];
    meta: Meta;
  }>;
  onSearch: (search: string) => void;
  searchValue: string;
  onSelectEvent: (event: Event) => void;
  noEventMessage?: string;
}

const EventItem = ({
  event,
  onSelect,
  locale,
}: {
  event: Event;
  onSelect: () => void;
  locale: Locale;
}) => {
  const dateStr = formatDateRange(event.startDate, event.endDate, locale);

  return (
    <IonItem onClick={onSelect}>
      <Avatar slot="start">
        {event?.eventIcon?.thumbnailUrl ? (
          <img src={event?.eventIcon?.thumbnailUrl} alt={t`Event Icon`} />
        ) : (
          <ImageIcon icon={imageOutline} />
        )}
      </Avatar>
      <IonLabel>
        <h2>{event.name}</h2>
        <h3>{dateStr}</h3>
      </IonLabel>
    </IonItem>
  );
};

const EventList = ({
  events,
  onSelect,
}: {
  events?: Event[];
  onSelect: (event: Event) => void;
}) => {
  const { locale } = useSelector(selector);
  const GROUP_LABELS = {
    [HAPPENING]: t`Happening Today`,
    [UPCOMING]: t`Upcoming`,
    [CONCLUDED]: t`Concluded (past 30 days)`,
  };

  if (!events) {
    return null;
  }

  const groupEvents = events.reduce(
    (accum, event) => {
      accum[event.group].push(event);

      return accum;
    },
    // skipcq: JS-0369
    {
      [HAPPENING]: [],
      [UPCOMING]: [],
      [CONCLUDED]: [],
    } as Record<EventGroup, Event[]>
  );

  return (
    <>
      {GROUPS.map(group => (
        <React.Fragment key={group}>
          {groupEvents[group].length > 0 && <IonListHeader>{GROUP_LABELS[group]}</IonListHeader>}
          {groupEvents[group].map(event => (
            <EventItem
              key={event.id}
              event={event}
              // skipcq: JS-0417, JS-0336
              onSelect={() => onSelect(event)}
              locale={locale}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

const List = ({
  eventsQueryResult,
  onSearch,
  searchValue,
  onSelectEvent,
  noEventMessage,
}: ListLayoutProps) => {
  const { role } = useSelector(selector);
  const { fetchNextPage, data, hasNextPage = false, isLoading, isFetching } = eventsQueryResult;

  useEffect(() => {
    //  override swipe to go back with this gesture
    //  to prevent going back to login page
    const el = document.getElementById(ID);
    if (!el) {
      return undefined;
    }
    const gesture = createGesture({
      el,
      gestureName: 'goback-swipe',
      gesturePriority: SWIPE_TO_GO_BACK_PRIORITY + 1,
    });
    gesture.enable(true);

    return () => {
      gesture.destroy();
    };
  }, []);

  return (
    <Layout
      id={ID}
      title={t`Select Event`}
      footer={
        role === UserRole.Staff ? (
          <ManageMobiButton
            expand="block"
            fill="outline"
            color="danger"
            routerLink={generateRoute(RoutePath.ManageMobi)}
          >
            ManageMobi
          </ManageMobiButton>
        ) : undefined
      }
    >
      <InfiniteList
        loadData={fetchNextPage}
        onSearch={onSearch}
        endOfList={!hasNextPage}
        isLoading={isFetching && isLoading}
      >
        {data?.pages?.[0]?.data?.length === 0 && (
          <Content>
            <Icon icon={searchValue ? searchIcon : calendarOutline} />
            <Title>
              <h2>{searchValue ? t`No Results Found` : t`No Events Available`}</h2>
            </Title>
            <IonText>{noEventMessage}</IonText>
          </Content>
        )}
        <EventList
          events={data?.pages.reduce((accum: Event[], page) => accum.concat(page.data), [])}
          onSelect={onSelectEvent}
        />
      </InfiniteList>
    </Layout>
  );
};

export default List;
